import React, { useState } from 'react';
import { X, ListMusic, GripHorizontal, Search } from 'lucide-react';
import clsx from 'clsx';

interface PlaylistPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onAddPlaylist?: (playlistData: any) => void;
}

// Draggable Playlist Wrapper
function DraggablePlaylist({ playlist, children, onClick, className }: { playlist: any, children: React.ReactNode, onClick: () => void, className?: string }) {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('element-type', 'playlist');
    e.dataTransfer.setData('playlist-data', JSON.stringify(playlist));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className={clsx(className, 'opacity-100')}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

import { useGetSavedPlaylistsQuery, useGetCmsPlaylistsQuery } from '../../features/playlists/playlistsApi';

// Helper function to format seconds into "[hours]h [minutes]m [seconds]s" (e.g. 5m, 1h 20m)
const formatDuration = (totalSeconds: number) => {
  if (!totalSeconds) return '0s';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);

  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);

  return parts.join(' ');
};

function Wrapper1({ children }: React.PropsWithChildren) {
  return (
    <div className="h-[40px] relative rounded-[6px] shrink-0 w-[48px]">
      <div className="overflow-clip relative rounded-[inherit] size-full flex items-center justify-center bg-gray-50">{children}</div>
      <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[6px]" />
    </div>
  );
}

export function PlaylistPanel({ isOpen, onClose, onAddPlaylist }: PlaylistPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'saved' | 'cms'>('cms');
  const [savedPage, setSavedPage] = useState(1);
  const [cmsPage, setCmsPage] = useState(1);

  // Fetch playlists from API based on active tab
  const savedQuery = useGetSavedPlaylistsQuery(
    { page: savedPage, limit: 20, search: searchQuery || undefined },
    { skip: activeTab !== 'saved' }
  );
  const cmsQuery = useGetCmsPlaylistsQuery(
    { page: cmsPage, limit: 20, search: searchQuery || undefined },
    { skip: activeTab !== 'cms' }
  );

  const currentQuery = activeTab === 'saved' ? savedQuery : cmsQuery;
  const { data: playlistResponse, isLoading, isFetching, isError } = currentQuery;
  const playlists = playlistResponse?.results || [];

  const hasMore = playlistResponse ? playlistResponse.results.length < playlistResponse.totalResults : false;

  // Reset pages when search changes
  React.useEffect(() => {
    setSavedPage(1);
    setCmsPage(1);
  }, [searchQuery]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !isFetching) {
      if (activeTab === 'saved') {
        setSavedPage(prev => prev + 1);
      } else {
        setCmsPage(prev => prev + 1);
      }
    }
  };

  const filteredPlaylists = playlists; // Filtering is now handled by API search param

  return (
    <>


      {/* Panel */}
      <div
        className={clsx(
          "fixed left-[98px] top-[80px] w-[340px] h-[calc(100vh-100px)] bg-white rounded-2xl shadow-xl z-40 flex flex-col transition-all duration-300 ease-in-out border border-gray-100 overflow-hidden",
          isOpen ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0 pointer-events-none'
        )}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 flex items-center justify-between bg-white z-10">
          <h2 className="font-semibold text-gray-900 text-lg">Playlists</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pb-2">
          <div className="flex p-1 rounded-xl bg-gray-50 border border-gray-100 relative overflow-hidden">
            <button
              onClick={() => setActiveTab('cms')}
              className={clsx(
                "flex-1 py-1.5 text-xs font-medium rounded-lg transition-all relative overflow-hidden group",
                activeTab === 'cms' ? "app-active-state" : "text-gray-500 hover:text-gray-700"
              )}
            >
              <div 
                  className="app-selector-indicator" 
                  style={{ '--background-width': activeTab === 'cms' ? '100%' : '0%' } as React.CSSProperties} 
              />
              <span className="relative z-10 transition-colors duration-400">CMS Playlists</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 pb-4">
          <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <Search className="w-4 h-4 text-gray-400 mr-2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search ${activeTab === 'saved' ? 'saved' : 'cms'} playlists...`}
              className="bg-transparent border-none outline-none text-sm text-gray-900 placeholder-gray-400 flex-1"
            />
          </div>
        </div>

        {/* Content */}
        <div
          className="flex-1 overflow-y-auto px-6 py-2 custom-scrollbar"
          onScroll={handleScroll}
        >
          <div className="flex flex-col gap-2">

            {isLoading && (
              <div className="flex items-center justify-center py-10">
                <div className="w-6 h-6 border-2 border-[#0ba29b] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {isError && !isLoading && (
              <div className="text-center py-8">
                <p className="text-red-500 text-sm">Failed to load playlists</p>
              </div>
            )}

            {!isLoading && !isError && filteredPlaylists.map((playlist) => (
              <DraggablePlaylist
                key={playlist._id}
                playlist={playlist}
                onClick={() => onAddPlaylist?.(playlist)}
                className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-xl border border-transparent hover:border-gray-200 transition-all group cursor-pointer"
              >
                <Wrapper1>
                  <ListMusic className="w-5 h-5 text-[#0ba29b]" />
                </Wrapper1>
                <div className="flex flex-col gap-0.5 overflow-hidden">
                  <p className="font-medium text-[#101928] text-[14px] leading-tight truncate">
                    {playlist.name}
                  </p>
                  <p className="text-[11px] text-[#667185]">
                    {playlist.total_content || 0} contents • {formatDuration(playlist.totalDuration)}
                  </p>
                </div>
                <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                  <div className="text-gray-400 p-1 hover:text-gray-600 cursor-grab active:cursor-grabbing">
                    <GripHorizontal className="w-4 h-4" />
                  </div>
                </div>
              </DraggablePlaylist>
            ))}

            {!isLoading && !isError && filteredPlaylists.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400 text-sm">No playlists found</p>
              </div>
            )}
          </div>

          {isFetching && playlistResponse && (
            <div className="flex justify-center py-4">
              <div className="w-6 h-6 border-2 border-[#0ba29b] border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>

      </div>
    </>
  );
}

